// @ts-check
import React, { useEffect, useRef, useState } from 'react';
// @ts-ignore
import PlusIcon from '../../utils/icon/icon-plus.svg';
// @ts-ignore
import CloseIcon from '../../utils/icon/icon-close.svg';
import Panel from '../../utils/panel';
import ListFacet from './list-facet';

// @ts-ignore
const locales = window.TMW.Strings;
const facetLocales = locales.collection.facets;
const accessibilityLocales = locales.accesssibility;

/**
 * @typedef {Object} FacetValue
 * @property {string} label - The label of the facet value.
 * @property {string} value - The value of the facet value.
 * @property {boolean} active - Whether the facet value is active.
 * @property {number} count - The count of the facet value.
 * @property {string} paramName - The parameter name of the facet value.
 * @property {string} urlToRemove - The URL to remove the facet value.
 * @property {string} urlToAdd - The URL to add the facet value.
 */

/**
 * @typedef {Object} Facet
 * @property {string} label - The label of the facet.
 * @property {string} type - The type of the facet.
 * @property {string} operator - The operator of the facet.
 * @property {string} paramName - The parameter name of the facet.
 * @property {string} urlToRemove - The URL to remove the facet.
 * @property {FacetValue[]} values - The values of the facet.
 * @property {FacetValue[]} activeValues - The active values of the facet.
 * @property {FacetValue[]} inactiveValues - The inactive values of the facet.
 */

/**
 * Filter component for product grids
 * @component
 * @param {Object} props
 * @param {string} props.urlToRemoveAll - The URL to remove all filters.
 * @param {Facet[]} props.facets
 * @param {(event: Event) => void} props.onChange - The function to call when the filter option changes.
 */

const Filter = ({ facets, onChange, handleClearFilters, disableApplyButtonState, isLoading }) => {
  const openTriggerEl = useRef(null);
  const closeTriggerEl = useRef(null);
  const [panelTriggers, setPanelTriggers] = useState({
    openTriggerEl: null,
    closeTriggerEl: null,
  });

  useEffect(() => {
    const newTriggers = {};

    if (openTriggerEl.current) newTriggers.openTriggerEl = openTriggerEl.current;
    if (closeTriggerEl.current) newTriggers.closeTriggerEl = closeTriggerEl.current;

    if (Object.keys(newTriggers).length > 0) {
      setPanelTriggers((value) => ({
        ...value,
        ...newTriggers,
      }));
    }
  }, [openTriggerEl, closeTriggerEl]);

  const handleApplyFiter = () => {
    document.dispatchEvent(new CustomEvent('filter:close'));
  };

  return (
    <div>
      <button
        className="body-1-bold flex items-center gap-2"
        ref={openTriggerEl}
      >
        <span>{facetLocales.filterLabel}</span>
        <span className="size-3">
          <PlusIcon />
        </span>
      </button>

      <Panel
        triggers={panelTriggers}
        defaultOpen={false}
        context="filter"
        alignment="left"
        overlay
        additionalClasses="lg:max-w-[470px]"
      >
        <div className="isolate flex h-full flex-col">
          <div className="mt-3 flex-1 overflow-y-auto px-3 pt-5 md:mt-0 lg:px-8">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="heading-4">{facetLocales.filterLabel}</h2>
              <button
                className="size-6"
                aria-label={accessibilityLocales.close}
                ref={closeTriggerEl}
              >
                <CloseIcon />
              </button>
            </div>
            <div>
              <div className="mt-3 flex flex-col gap-2 md:gap-2.5">
                {facets.map((facet) => (
                  <ListFacet
                    key={facet.label}
                    facet={facet}
                    onChange={onChange}
                    isLoading={isLoading}
                  />
                ))}
              </div>
            </div>
          </div>
          <div className="sticky bottom-0 z-1 bg-inverse px-3 py-5 md:px-8">
            <div className="flex items-center gap-16">
              {disableApplyButtonState && (
                <button
                  onClick={handleClearFilters}
                  className="link whitespace-nowrap"
                  disabled={!disableApplyButtonState}
                >
                  {facetLocales.clearAll}
                </button>
              )}
              <button
                className="button button--primary w-full"
                onClick={handleApplyFiter}
                disabled={!disableApplyButtonState}
              >
                {facetLocales.applyFilters}
              </button>
            </div>
          </div>
        </div>
      </Panel>
    </div>
  );
};

export default Filter;
